import type {
  RealEstateEnrichmentRequest,
  RealEstateIntake,
  RealEstateLineItem,
  RealEstateLineItemCalcType,
  RealEstateLineItemSection,
  RealEstatePropertyType,
  RealEstateScenario,
  RealEstateScenarioID,
  RealEstateScenarioName,
} from "@features/real-estate/types";

export const REAL_ESTATE_TABS = [
  { label: "Intake", path: "/real-estate/intake" },
  { label: "Context", path: "/real-estate/context" },
  { label: "Underwrite", path: "/real-estate/underwrite" },
  { label: "Results", path: "/real-estate/results" },
] as const;

export const REAL_ESTATE_PROPERTY_TYPES: Array<{ value: RealEstatePropertyType; label: string; }> = [
  { value: "single_family", label: "Single-family" },
  { value: "condo", label: "Condo" },
  { value: "townhome", label: "Townhome" },
  { value: "duplex", label: "Duplex" },
  { value: "triplex", label: "Triplex" },
  { value: "fourplex", label: "Fourplex" },
  { value: "small_multifamily", label: "Small multifamily" },
];

export const REAL_ESTATE_LINE_ITEM_SECTIONS: Array<{ value: RealEstateLineItemSection; label: string; }> = [
  { value: "acquisition", label: "Acquisition" },
  { value: "operating", label: "Operating" },
  { value: "reserve", label: "Reserves" },
  { value: "capex", label: "Capex" },
  { value: "exit", label: "Exit" },
];

export const REAL_ESTATE_LINE_ITEM_CALC_TYPES: Array<{ value: RealEstateLineItemCalcType; label: string; }> = [
  { value: "fixed_annual", label: "Fixed annual" },
  { value: "fixed_monthly", label: "Fixed monthly" },
  { value: "one_time", label: "One-time" },
  { value: "percent_of_gross_rent", label: "% of gross rent" },
  { value: "percent_of_egi", label: "% of EGI" },
  { value: "percent_of_purchase_price", label: "% of purchase" },
  { value: "percent_of_loan", label: "% of loan" },
  { value: "percent_of_rehab", label: "% of rehab" },
  { value: "percent_of_sale_price", label: "% of sale price" },
];

export const REAL_ESTATE_DEFAULTS = {
  holdPeriodMonths: 60,
  vacancyRate: 5,
  propertyManagementPercent: 8,
  maintenancePercent: 5,
  capitalReservesPercent: 5,
  annualRentGrowthRate: 2,
  annualExpenseGrowthRate: 2,
  appreciationRate: 3,
  saleCostsPercent: 6,
  rehabContingencyPercent: 10,
  interestRate: 6.75,
  downPaymentPercent: 25,
  amortizationYears: 30,
} as const;

export function createInitialIntake(): RealEstateIntake {
  return {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    listingUrl: "",
    propertyType: "single_family",
    askingPrice: 0,
    targetOffer: 0,
    unitCount: 1,
    bedrooms: 0,
    bathrooms: 0,
    squareFeet: 0,
    yearBuilt: 0,
    annualPropertyTax: 0,
    annualInsurance: 0,
    lightRehab: false,
  };
}

export function buildEnrichmentRequest(intake: RealEstateIntake): RealEstateEnrichmentRequest {
  return {
    addressLine1: intake.addressLine1.trim(),
    addressLine2: intake.addressLine2.trim(),
    city: intake.city.trim(),
    state: intake.state.trim().toUpperCase(),
    postalCode: intake.postalCode.trim(),
    listingUrl: intake.listingUrl.trim(),
    propertyType: intake.propertyType,
    askingPrice: intake.askingPrice,
    targetOffer: intake.targetOffer,
    unitCount: intake.unitCount,
    bedrooms: intake.bedrooms,
    bathrooms: intake.bathrooms,
    squareFeet: intake.squareFeet,
    yearBuilt: intake.yearBuilt,
    annualPropertyTax: intake.annualPropertyTax,
    annualInsurance: intake.annualInsurance,
  };
}

export function createScenario(name: RealEstateScenarioName, id: RealEstateScenarioID, intake: RealEstateIntake): RealEstateScenario {
  const purchasePrice = intake.targetOffer > 0 ? intake.targetOffer : intake.askingPrice;
  const defaultRehab = intake.lightRehab ? 15000 : 0;

  return {
    id,
    name,
    financing: {
      purchasePrice,
      downPaymentPercent: REAL_ESTATE_DEFAULTS.downPaymentPercent,
      interestRate: REAL_ESTATE_DEFAULTS.interestRate,
      amortizationYears: REAL_ESTATE_DEFAULTS.amortizationYears,
    },
    income: {
      monthlyRent: 0,
      otherMonthlyIncome: 0,
      vacancyRate: REAL_ESTATE_DEFAULTS.vacancyRate,
      annualRentGrowthRate: REAL_ESTATE_DEFAULTS.annualRentGrowthRate,
    },
    operatingExpenses: {
      annualPropertyTax: intake.annualPropertyTax,
      annualInsurance: intake.annualInsurance,
      propertyManagementPercent: REAL_ESTATE_DEFAULTS.propertyManagementPercent,
      maintenancePercent: REAL_ESTATE_DEFAULTS.maintenancePercent,
      annualExpenseGrowthRate: REAL_ESTATE_DEFAULTS.annualExpenseGrowthRate,
    },
    reserves: {
      capitalReservesPercent: REAL_ESTATE_DEFAULTS.capitalReservesPercent,
    },
    rehabCapex: {
      rehabBudget: defaultRehab,
      rehabContingencyPercent: REAL_ESTATE_DEFAULTS.rehabContingencyPercent,
    },
    exitAssumptions: {
      holdPeriodMonths: REAL_ESTATE_DEFAULTS.holdPeriodMonths,
      appreciationRate: REAL_ESTATE_DEFAULTS.appreciationRate,
      saleCostsPercent: REAL_ESTATE_DEFAULTS.saleCostsPercent,
    },
    lineItems: createDefaultLineItems(defaultRehab),
  };
}

export function cloneScenario(base: RealEstateScenario, name: RealEstateScenarioName, id: RealEstateScenarioID): RealEstateScenario {
  return {
    ...base,
    id,
    name,
    financing: { ...base.financing },
    income: { ...base.income },
    operatingExpenses: { ...base.operatingExpenses },
    reserves: { ...base.reserves },
    rehabCapex: { ...base.rehabCapex },
    exitAssumptions: { ...base.exitAssumptions },
    lineItems: base.lineItems.map((item) => ({ ...item })),
  };
}

export function createCustomLineItem(section: RealEstateLineItemSection): RealEstateLineItem {
  return {
    id: `${section}-${Math.random().toString(36).slice(2, 10)}`,
    section,
    label: `Custom ${section}`,
    calcType: section === "operating" || section === "reserve" ? "fixed_annual" : "one_time",
    amount: 0,
    enabled: true,
    removable: true,
  };
}

function createDefaultLineItems(defaultRehab: number): RealEstateLineItem[] {
  return [
    lineItem("attorney-fee", "acquisition", "Attorney fee", "one_time", 1500, false),
    lineItem("title-recording", "acquisition", "Title, escrow, recording", "one_time", 2200, false),
    lineItem("inspection-appraisal", "acquisition", "Inspection and appraisal", "one_time", 900, false),
    lineItem("buyer-agent", "acquisition", "Buyer-side agent fee or concession", "percent_of_purchase_price", 0, false),
    lineItem("hoa-dues", "operating", "HOA dues", "fixed_annual", 0, false),
    lineItem("owner-utilities", "operating", "Owner-paid utilities", "fixed_annual", 0, false),
    lineItem("other-operating", "operating", "Other operating costs", "fixed_annual", 1200, false),
    lineItem("turnover-buffer", "reserve", "Turnover and leasing buffer", "fixed_annual", 1200, false),
    lineItem("permits-design", "capex", "Permits and design", "one_time", defaultRehab > 0 ? 1500 : 0, false),
    lineItem("exit-attorney", "exit", "Exit attorney and closing", "one_time", 1200, false),
  ];
}

function lineItem(
  id: string,
  section: RealEstateLineItemSection,
  label: string,
  calcType: RealEstateLineItemCalcType,
  amount: number,
  removable: boolean,
): RealEstateLineItem {
  return { id, section, label, calcType, amount, enabled: true, removable };
}
